<template>
  <!-- Recurring Monthly confirmation overlay.
       Absolute-positioned inside the form pane. Dims the form behind so
       the user can't interact with it until they either confirm monthly
       or switch back to one-time. No layout displacement. -->
  <transition name="monthly-overlay">
    <div
      v-if="showMonthlyOverlay"
      class="monthly-overlay"
      role="alertdialog"
      aria-labelledby="monthly-overlay-title"
      aria-describedby="monthly-overlay-desc"
    >
      <div class="monthly-overlay__backdrop" @click="switchToOneTime" aria-hidden="true"></div>
      <div class="monthly-overlay__panel">
        <div class="monthly-overlay__icon" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 2v6h-6"></path>
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
            <path d="M3 22v-6h6"></path>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
          </svg>
        </div>
        <p class="monthly-overlay__eyebrow">Confirm your choice</p>
        <h3 id="monthly-overlay-title" class="monthly-overlay__title">Switch to Monthly?</h3>
        <p id="monthly-overlay-desc" class="monthly-overlay__desc">
          Your card will be charged <strong>every month</strong> until you choose to cancel.
        </p>
        <div class="monthly-overlay__actions">
          <button
            type="button"
            class="monthly-overlay__btn monthly-overlay__btn--primary"
            @click="confirmMonthly"
            ref="confirmBtn"
          >
            Yes, continue monthly
          </button>
          <button
            type="button"
            class="monthly-overlay__btn monthly-overlay__btn--ghost"
            @click="switchToOneTime"
          >
            No, make it one-time
          </button>
        </div>
      </div>
    </div>
  </transition>

  <div class="px-10 flex flex-col h-full pb-10">
    <div class="space-y-3">
      <fieldset>
        <div class="flex items-center justify-center">
          <span class="font-medium">One-Time</span>
          <div class="relative px-3">
            <button
              @click="toggleFrequency()"
              id="frequency-switch"
              type="button"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-300 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-200 focus-visible:ring-offset-1 active:scale-95"
              v-bind:class="toggleClassObject"
              role="switch"
              :aria-checked="isMonthly ? 'true' : 'false'"
            >
              <span class="sr-only">Use setting</span>
              <span
                aria-hidden="true"
                class="toggle-thumb switcher pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,0.18),0_0_0_1px_rgba(0,0,0,0.04)] ring-0 transition duration-300 ease-in-out"
                :key="isMonthly ? 'thumb-on' : 'thumb-off'"
                v-bind:class="togglePositionClassObject"
              ></span>
            </button>
          </div>
          <span class="font-medium" style="display: inline-flex; align-items: center; overflow: visible; gap: 0.375rem;">
            Monthly
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#DA1884" style="width: 1em; height: 1em; display: inline-block; vertical-align: -0.125em;" aria-hidden="true">
              <path d="M12 21s-7.19-4.35-10-9.54C.47 7.93 3.13 4 7.07 4a5 5 0 0 1 4.93 3.07A5 5 0 0 1 16.93 4C20.87 4 23.53 7.93 22 11.46 19.19 16.65 12 21 12 21z"/>
            </svg>
          </span>
        </div>
      </fieldset>


      <fieldset>
        <div class="flex flex-col mt-3">
          <div class="mb-4"><label class="block text-base font-medium text-gray-700">How much would you like to donate?</label></div>
          <!-- `:class="pillClass(val)"` is the authoritative highlight
               source; it reacts directly to Vuex `selectedAmount` and
               doesn't rely on Tailwind peer-checked (which can flash
               gray during Vue hydration before v-model settles). -->
          <div class="switch-field grid gap-x-2 gap-y-2 grid-cols-3" v-show="isOnce">
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_1" value="5000" name="donationAmount_oneTime" /><label :class="pillClass('5000')" class="amount-pill" for="switch_1">$50</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" checked="checked" class="hidden peer" type="radio" id="switch_2" value="7500" name="donationAmount_oneTime" /><label :class="pillClass('7500')" class="amount-pill" for="switch_2"> $75 </label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_3" value="12500" name="donationAmount_oneTime" /><label :class="pillClass('12500')" class="amount-pill" for="switch_3">$125</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_4" value="25000" name="donationAmount_oneTime" /><label :class="pillClass('25000')" class="amount-pill" for="switch_4">$250</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_5" value="50000" name="donationAmount_oneTime" /><label :class="pillClass('50000')" class="amount-pill" for="switch_5">$500</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" id="switch_6" type="radio" value="100000" name="donationAmount_oneTime" /><label :class="pillClass('100000')" class="amount-pill" for="switch_6">$1000</label></div>
          </div>
          <div class="switch-field grid gap-x-2 gap-y-2 grid-cols-3" v-show="isMonthly">
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_7" value="1500" name="donationAmount_monthly" /><label :class="pillClass('1500')" class="amount-pill" for="switch_7">$15</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" checked="checked" class="hidden peer" type="radio" id="switch_8" value="2500" name="donationAmount_monthly" /><label :class="pillClass('2500')" class="amount-pill" for="switch_8"> $25 </label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_9" value="5000" name="donationAmount_monthly" /><label :class="pillClass('5000')" class="amount-pill" for="switch_9">$50</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_10" value="7500" name="donationAmount_monthly" /><label :class="pillClass('7500')" class="amount-pill" for="switch_10">$75</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" type="radio" id="switch_11" value="10000" name="donationAmount_monthly" /><label :class="pillClass('10000')" class="amount-pill" for="switch_11">$100</label></div>
            <div><input v-model="selectedAmount" @change="updateDonationValue" class="hidden peer" id="switch_12" type="radio" value="15000" name="donationAmount_monthly" /><label :class="pillClass('15000')" class="amount-pill" for="switch_12">$150</label></div>
          </div>
          <div class="my-3">
            <div class="relative mt-1 rounded-md shadow-sm">
              <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <span class="text-black mt-1 font-[family-name:Red_Hat_Display] font-medium">$</span>
              </div>
              <input
                type="text"
                v-model="enteredOtherAmount"
                @input="updateDonationValueOther"
                :class="{' border-red-500': errors.includes('other-amount') === true, 'border-gray-160': errors.includes('other-amount') === false }"
                name="other-amount"
                id="alt_amount_input"
                class="border block w-full border-gray-200 pl-7 pr-12 h-12 text-4xl md:text-3xl font-semibold text-pink placeholder-pink-200 font-[family-name:Red_Hat_Display] focus:border-pink-400 focus:outline-none focus:ring-2 focus:ring-pink-100 rounded-md transition-colors duration-200"
                placeholder="75"
                autofocus
              >
              <div class="absolute inset-y-0 right-0 flex items-center">
                <label for="currency" class="sr-only">Currency</label>
                <select id="currency" name="currency" class="h-full border-transparent bg-transparent font-semibold py-0 pl-2 pr-7 text-gray-500 selection:text-black focus:border-transparent focus:ring-0 sm:text-sm">
                  <option>USD</option>
                  <option>CAD</option>
                  <option>EUR</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </fieldset>
      
      <fieldset class="space-y-2 mb-3">
        <div class="relative flex items-start" v-if="this.donationWidgetState.occurrence == 'once'">
          <div class="flex h-5 items-center">
            <input
              v-model="donationWidgetState.paidForTransactionFee"
              id="processingFee"
              name="agreement"
              type="checkbox"
              class="transition-all ease-in-out duration-300 h-4 w-4 border-gray-200 focus:ring-0 rounded-md cursor-pointer"
            >
          </div>
          <div class="ml-3 text-sm">
            <label for="processingFee" class="text-black font-normal text-base cursor-pointer">I would like to cover the processing fee of <span class="currency-code text-pink-500">$</span><span id="donation-fee" class="font-semibold text-pink-500">{{calculatedFee}}</span> so all of my donation goes to Side-Out Foundation</label>
          </div>
        </div>
        <!-- Monthly agreement checkbox moved to the prominent Recurring
             Monthly notice above the amount grid. See .monthly-notice block. -->

        <div class="relative flex items-start">
          <div class="flex h-5 items-center">
            <input
              @change="resetTributeOpenStatus"
              v-model="donationWidgetState.hasDedication"
              id="tribute"
              name="tribute" type="checkbox"
              class="transition-all ease-in-out duration-300 h-4 w-4 border-gray-200 focus:ring-0 rounded-md cursor-pointer"
            >
          </div>
          <div class="ml-3 text-sm flex-1 flex items-center justify-between gap-2">
            <label for="tribute" class="text-black font-normal text-base cursor-pointer">Give in honor or memory of a loved one</label>
            <!-- Appears once a tribute has been saved AND the panel
                 is closed. One-click reopen, bypassing the checkbox
                 toggle which would otherwise need two clicks. -->
            <button
              v-if="donationWidgetState.hasDedication && closeTribute"
              type="button"
              class="tribute-edit-link"
              @click.prevent="openTributeForEdit"
            >Edit</button>
          </div>
        </div>
      </fieldset>
      
      <fieldset class="mt-auto space-y-0">
        <button
          @click.prevent="next"
          class="transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] bg-pink-500 hover:bg-pink-700 active:scale-[0.98] text-white uppercase py-2 px-4 w-full font-bold mt-2 rounded-full font-[family-name:Red_Hat_Display] tracking-wide text-lg"
        >
          Donate <span>${{ donationAmountForView }}</span><template v-if="isMonthly"> / month</template>
        </button>
      </fieldset>
      
      <input type="hidden" id="donationFrequency" v-model="donationWidgetState.occurrence">
    </div>
  </div>
  
  <!-- Tribute swing: always mounted so CSS transitions actually fire
       on `is-open` toggle. A v-if'd element mounts with its final
       state already applied and skips the transition entirely — that
       was the "no slide-in" bug. Panel fills the full form column
       via inset:0 so the dedication takes the full stage with no
       empty margin at the top. -->
  <div
    id="tribute-swing"
    class="tribute-swing"
    :class="{ 'is-open': isTributeOpen }"
    role="dialog"
    aria-labelledby="tribute-title"
    :aria-hidden="isTributeOpen ? 'false' : 'true'"
    @keydown.esc="cancelTribute"
  >
    <!-- Backdrop sits within the form column only (absolute to the
         tribute-swing). Click dismisses as a cancel. -->
    <div class="tribute-swing__backdrop" @click="cancelTribute" aria-hidden="true" />

    <div class="tribute-swing__panel" :data-step="tributeStep">
      <!-- ─── STEP 1 — Choose tribute type ─────────────────────────
           The honor/memory choice is the user's first and only
           decision on this screen. Hero-sized heart icon owns the
           top half; segmented toggle is the focal control; one CTA
           sends them to step 2 where they fill in the details. -->
      <transition name="tribute-step" mode="out-in">
        <div v-if="tributeStep === 1" key="step-1" class="tribute-swing__stage tribute-swing__stage--choice">
          <header class="tribute-swing__hero">
            <span class="tribute-swing__halo" aria-hidden="true">
              <span class="tribute-swing__halo-ring" aria-hidden="true" />
              <span class="tribute-swing__icon tribute-swing__icon--hero" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="40" height="40" fill="currentColor" aria-hidden="true">
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
                </svg>
              </span>
            </span>
            <span class="tribute-swing__eyebrow">{{ tributeMode === 'memory' ? 'In Loving Memory' : 'In Honor' }}</span>
            <h3 id="tribute-title" class="tribute-swing__title">Share your tribute</h3>
            <p class="tribute-swing__desc">Dedicate this gift to someone meaningful. Choose how you'd like to honor them.</p>
          </header>

          <!-- Segmented memory/honor toggle — sole decision on this step.
               Memory is first and default since most tribute donations
               on this campaign are made in memory of someone. -->
          <div class="tribute-swing__segmented tribute-swing__segmented--lg" role="tablist" aria-label="Tribute type">
            <button
              type="button"
              role="tab"
              :aria-selected="tributeMode === 'memory'"
              class="tribute-swing__seg-btn"
              :class="{ 'is-active': tributeMode === 'memory' }"
              @click="setTributeMode('memory')"
            >In Memory Of</button>
            <button
              type="button"
              role="tab"
              :aria-selected="tributeMode === 'honor'"
              class="tribute-swing__seg-btn"
              :class="{ 'is-active': tributeMode === 'honor' }"
              @click="setTributeMode('honor')"
            >In Honor Of</button>
            <span
              class="tribute-swing__seg-indicator"
              :style="{ transform: tributeMode === 'honor' ? 'translateX(100%)' : 'translateX(0)' }"
              aria-hidden="true"
            />
          </div>

          <div class="tribute-swing__step-actions">
            <button
              type="button"
              class="tribute-swing__save tribute-swing__save--block"
              @click.prevent="goToTributeStep(2)"
            >Continue</button>
            <button
              type="button"
              class="tribute-swing__cancel"
              @click.prevent="cancelTribute"
            >Cancel</button>
          </div>
        </div>

        <!-- ─── STEP 2 — Honoree details ─────────────────────────
             Form-fill stage. Compact context strip at top reminds
             the user which mode they're in (with a back-arrow
             affordance) so the toggle doesn't have to repeat here. -->
        <div v-else key="step-2" class="tribute-swing__stage tribute-swing__stage--form">
          <button
            type="button"
            class="dw-back-pill tribute-swing__back-anchor"
            @click.prevent="goToTributeStep(1)"
            :aria-label="`Back to tribute type. Currently ${tributeMode === 'memory' ? 'In Memory Of' : 'In Honor Of'}`"
          >
            <svg class="dw-back-pill__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            <span class="dw-back-pill__label">{{ tributeMode === 'memory' ? 'In Memory Of' : 'In Honor Of' }}</span>
            <span class="dw-back-pill__edit" aria-hidden="true">change</span>
          </button>

          <header class="tribute-swing__form-header">
            <span class="tribute-swing__icon tribute-swing__icon--inline" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
              </svg>
            </span>
            <h3 id="tribute-title" class="tribute-swing__form-title">Tell us about them</h3>
          </header>

          <!-- Fields -->
          <div class="tribute-swing__fields">
            <fieldset class="tribute-swing__field">
              <label for="dedication_honoree_name" class="tribute-swing__label">{{ honoreeNameLabel }}</label>
              <input
                type="text"
                class="tribute-swing__input"
                :class="{ 'has-error': errors.includes('dedication_honoree_name') }"
                v-model="donationWidgetState.honoreeName"
                id="dedication_honoree_name"
                name="dedication_honoree_name"
                autocomplete="off"
              >
            </fieldset>

            <fieldset v-if="inHonorOfSelected" class="tribute-swing__field">
              <label for="dedication_honoree_email" class="tribute-swing__label">{{ honoreeEmailLabel }}</label>
              <input
                type="email"
                class="tribute-swing__input"
                :class="{ 'has-error': errors.includes('dedication_honoree_email') }"
                v-model="donationWidgetState.honoreeEmail"
                id="dedication_honoree_email"
                name="dedication_honoree_email"
                autocomplete="email"
              >
            </fieldset>

            <fieldset class="tribute-swing__field">
              <label for="message_to_honoree" class="tribute-swing__label">{{ honoreeMessageLabel }}</label>
              <textarea
                class="tribute-swing__input tribute-swing__textarea"
                :class="{ 'has-error': errors.includes('message_to_honoree') }"
                v-model="donationWidgetState.honoreeMessage"
                name="message_to_honoree"
                id="message_to_honoree"
                rows="3"
              ></textarea>
            </fieldset>
          </div>

          <div class="tribute-swing__step-actions">
            <button
              type="button"
              class="tribute-swing__save tribute-swing__save--block"
              @click.prevent="saveTribute"
              id="save-tribute"
            >Save tribute</button>
            <button
              type="button"
              class="tribute-swing__cancel"
              @click.prevent="cancelTribute"
            >Cancel</button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import BigNumber from 'bignumber.js'

export default {
  name: 'donation-form',

  data() {
    return {
      errors: [],
      showClassA: true,
      donationValue: '',
      tributeMode: 'memory',
      // Two-step tribute swing: 1 = honor/memory choice (hero icon
      // + segmented toggle), 2 = honoree details form. Resets to 1
      // whenever the swing closes so re-opening always lands on the
      // primary decision.
      tributeStep: 1,
      closeTribute: true,
      // True once the user has explicitly confirmed recurring monthly via
      // the overlay. Serves the same purpose as the old agreement checkbox
      // (required before submit) but is captured by the overlay's Yes
      // button rather than a tiny form checkbox.
      monthlyAgreement: false,
      // Shows the full-pane overlay asking for explicit monthly confirmation.
      showMonthlyOverlay: false,
    }
  },
  computed: {
    toggleClassObject() {
      return {
        // Standard toggle semantics: gray = off (one-time), brand = on (monthly).
        // Previously used green-500 which reads as "good/success" next to monthly.
        'bg-gray-300': this.donationWidgetState.occurrence == 'once',
        'bg-pink': this.donationWidgetState.occurrence == 'monthly',
      }
    },
    togglePositionClassObject() {
      return {
        'translate-x-0': this.donationWidgetState.occurrence == 'once',
        'translate-x-5': this.donationWidgetState.occurrence == 'monthly',
      }
    },
    donationWidgetState() {
      return this.$store.getters.donationWidget
    },
    selectedAmount: {
      get() {
        return this.donationWidgetState.amount
      },
      set(value) {
        this.$store.dispatch('updateAmount', value)
      },
    },
    otherAmountInView: {
      get() {
        return this.donationWidgetState.otherAmountInView
      },
      set(value) {
        this.$store.dispatch('updateOtherAmountInView', value)
      },
    },
    honoreeNameLabel() {
      if (this.donationWidgetState.inHonorOfSelection === 'memory') {
        return 'In memory of'
      }
      return `In honor of`
    },
    honoreeEmailLabel() {
      if (this.donationWidgetState.inHonorOfSelection === 'memory') {
        return `Recipient's email`
      }
      return `Honoree's email`
    },
    honoreeMessageLabel() {
      if (this.donationWidgetState.inHonorOfSelection === 'memory') {
        return `Message`
      }
      return `Message to honoree`
    },
    inHonorOfSelected() {
      return this.donationWidgetState.inHonorOfSelection !== 'memory'
    },
    calculatedAmount() {
      if (this.otherAmountInView == 'other') {
        if (!this.enteredOtherAmount) {
          return 0
        }
        return new BigNumber(this.enteredOtherAmount).times(100).toNumber()
      }
      return this.selectedAmount
    },
    enteredOtherAmount: {
      get() {
        return this.donationWidgetState.enteredOtherAmount
      },
      set(value) {
        this.$store.dispatch('updateEnteredOtherAmount', value)
      },
    },
    donationAmountForView() {
      return new BigNumber(this.calculatedAmount).dividedBy(100).toFormat(2)
    },
    isMonthly() {
      return this.donationWidgetState.occurrence == 'monthly'
    },
    isOnce() {
      return this.donationWidgetState.occurrence == 'once'
    },
    computedSelectedOption: function () {
        return (this.donationValue / 100).toString();
    },
    // Reactive selected-pill class. Replaces the Tailwind
    // peer-checked variant so the pink highlight doesn't depend on
    // CSS sibling state that can lag during Vue hydration.
    pillClass() {
      return (value) => ({
        'amount-pill--active': String(this.selectedAmount) === String(value),
      })
    },
    // True when the tribute swing should be visibly open. Separated
    // from hasDedication (the persisted choice) + closeTribute (UI
    // dismissal) so the open/close pair can be reasoned about in one
    // boolean. Keeps the element mounted during the close animation.
    isTributeOpen() {
      return this.donationWidgetState.hasDedication && this.closeTribute !== true
    },
    // Processing-fee amount that gets added when the donor opts to
    // cover Stripe's cost. Derives from Stripe's 2.9% + $0.30 formula
    // solved for the gross charge that nets the intended donation:
    //   gross = (goal + fixedFee) / (1 - percentageFee)
    //   fee   = gross - goal
    // Returned as a BigNumber in dollars (not cents).
    calculatedFee() {
      const fixedFee = '30' // in cents
      const percentageFee = new BigNumber(0.022) // percentage
      const one = new BigNumber(1)

      const goal = new BigNumber(this.calculatedAmount)
      const calculatedCharge = goal.plus(fixedFee).dividedBy(one.minus(percentageFee))
      return BigNumber(calculatedCharge.minus(goal).dividedBy(100).toFormat(2))
    },
    // Final charge shown to the donor — base amount + calculatedFee
    // when the cover-fee checkbox is checked, base amount otherwise.
    calculatedTotal() {
      const amount = new BigNumber(this.donationWidgetState.amount)

      if (this.donationWidgetState.paidForTransactionFee) {
        return BigNumber(amount.plus(this.calculatedFee))
      }
      return amount
    },
    showMonthlyCheckbox() {
      return this.donationWidgetState.occurrence === 'monthly'
    },
  },

  watch: {
    // Broadcast the tribute open/close state so MakeDonationWidget can
    // hide the stepper while the user is focused on the tribute form.
    isTributeOpen: {
      immediate: true,
      handler(value) {
        this.emitter.emit(value ? 'tribute-opened' : 'tribute-closed')
      },
    },
  },
  methods: {
    updateTributeState(event) {
      this.donationWidgetState.inHonorOfSelection = event.target.value
    },
    updateDonationValueOther() {
      this.otherAmountInView = 'other'
      this.donationValue = this.enteredOtherAmount
      this.selectedAmount = this.enteredOtherAmount * 100
    },
    updateDonationValue() {
      this.donationValue = this.selectedAmount / 100
      this.enteredOtherAmount = this.donationValue
    },
    setMonthlyPopularAmount() {
      this.selectedAmount = 2500
      this.enteredOtherAmount = 25
    },
    setOncePopularAmount() {
      this.selectedAmount = 7500
      this.enteredOtherAmount = 75
    },
    setOtherAmountInView() {
      this.selectedAmount = ''
      this.otherAmountInView = 'other'
    },
    toggleFrequency() {
      this.donationWidgetState.occurrence = this.donationWidgetState.occurrence === 'once' ? 'monthly' : 'once'
      if ( this.donationWidgetState.occurrence == 'once' ) {
        this.setOncePopularAmount()
        // Reset agreement so re-entering monthly requires a fresh confirmation
        this.monthlyAgreement = false
        this.showMonthlyOverlay = false
      } else {
        this.setMonthlyPopularAmount()
        // Open the confirmation overlay. The overlay dims the form and
        // requires the user to pick Yes (confirmMonthly) or No
        // (switchToOneTime) before they can interact with the amount grid.
        this.showMonthlyOverlay = true
        // Focus the primary action for keyboard users.
        this.$nextTick(() => {
          if (this.$refs.confirmBtn && typeof this.$refs.confirmBtn.focus === 'function') {
            this.$refs.confirmBtn.focus()
          }
        })
      }
    },
    confirmMonthly() {
      this.monthlyAgreement = true
      this.showMonthlyOverlay = false
      // Clear any prior validation flag so the form doesn't stay red
      this.errors = this.errors.filter(e => e !== 'monthly-agreement')
    },
    // Switch-back used by both the overlay and the persistent chip.
    switchToOneTime() {
      this.showMonthlyOverlay = false
      if (this.donationWidgetState.occurrence !== 'once') {
        this.donationWidgetState.occurrence = 'once'
        this.setOncePopularAmount()
        this.monthlyAgreement = false
      }
    },
    saveTribute() {
      this.errors = []

      if (this.donationWidgetState.hasDedication) {
        if (!this.donationWidgetState.honoreeName) {
          this.errors.push('dedication_honoree_name')
        }
        
        if(this.donationWidgetState.inHonorOfSelection == 'honor') {
          if (!this.donationWidgetState.honoreeEmail || !this.validEmail()) {
            this.errors.push('dedication_honoree_email')
          }
        }
        
        if (!this.donationWidgetState.honoreeMessage) {
          this.errors.push('message_to_honoree')
        }
      }

      if( this.errors.length > 0) {
        this.donationWidgetState.steps.donation.isValid = false

        this.$notify({
          text: 'Please fix the validation errors',
          type: 'error',
          group: 'donationWidget',
        })
        return
      } else {
        this.closeTribute = true
        // Reset for the next open. If the user re-enters via Edit,
        // openTributeForEdit() sets it back to 2 explicitly.
        this.tributeStep = 1
      }

      return
    },

    resetTributeOpenStatus(event) {
      // Reset the closeTribute variable to false when the checkbox is engaged again -- this prevents the window from getting stuck in a closed state
      this.closeTribute = false
      // Always start a fresh swing on step 1.
      this.tributeStep = 1
    },
    // Honor/memory segmented toggle replaces the old <select>. Reuses
    // the existing updateTributeState() so the Vuex store stays aligned.
    setTributeMode(mode) {
      this.tributeMode = mode
      this.updateTributeState({ target: { value: mode } })
    },
    // Move between the two screens of the tribute swing. Step 1 is
    // the honor/memory choice; step 2 is the honoree-details form.
    goToTributeStep(step) {
      this.tributeStep = step
    },
    // Ghost Cancel button: abandon the tribute, untick the main-form
    // checkbox, slide back down. No validation. Reset to step 1 so
    // a future re-open lands on the primary choice.
    cancelTribute() {
      this.errors = []
      this.closeTribute = true
      this.tributeStep = 1
      this.donationWidgetState.hasDedication = false
    },
    // One-click reopen after a saved tribute. Bypasses the checkbox
    // state (which would require uncheck-then-recheck to trigger
    // resetTributeOpenStatus again). Returns to step 2 because the
    // user's intent here is to edit existing details, not re-pick.
    openTributeForEdit() {
      this.closeTribute = false
      this.tributeStep = 2
    },
    next() {
      this.errors = []
      
      if (this.otherAmountInView == 'other') {
        if ( !this.enteredOtherAmount) {
          this.errors.push('other-amount')
        }
        if (!this.validMoney(this.enteredOtherAmount)) {
          this.errors.push('other-amount')
        }
      } else {
        if (!this.validMoney(this.selectedAmount)) {
          this.errors.push('other-amount')
        }
      }
    
      if (this.showMonthlyCheckbox && !this.monthlyAgreement) {
        this.errors.push('monthly-agreement')
        // Safety net: the overlay is the only path to set monthlyAgreement.
        // If it's somehow false at submit time (e.g. state sync issue),
        // re-open the overlay so the user has an obvious way forward.
        this.showMonthlyOverlay = true
      }
    
      if( this.errors.length > 0) {
        this.donationWidgetState.steps.donation.isValid = false
    
        this.$notify({
          text: 'Please fix the validation errors',
          type: 'error',
          group: 'donationWidget',
        })
        return
      }
    
      this.$store.dispatch('updateDonationData', {
        hasDedication:  this.donationWidgetState.hasDedication,
        occurrence:  this.donationWidgetState.occurrence,
        otherAmountInView:  this.donationWidgetState.otherAmountInView,
        isAnonymous:  this.donationWidgetState.isAnonymous,
        inHonorOfSelection:  this.donationWidgetState.inHonorOfSelection,
        honoreeName:  this.donationWidgetState.honoreeName,
        honoreeEmail:  this.donationWidgetState.honoreeEmail,
        honoreeMessage:  this.donationWidgetState.honoreeMessage,
      })
    
      this.$store.dispatch('updateAmount', this.calculatedAmount)
    
      this.donationWidgetState.steps.donation.isInView = false
      this.donationWidgetState.steps.donation.isValid = true
      this.donationWidgetState.steps.payment.isInView = true
      
      this.donationWidgetState.enterFromDirection = 'translate-x-full'
      this.donationWidgetState.leaveToDirection = '-translate-x-full'
      
      return
    },
    validEmail() {
      var re = /\S+@\S+\.\S+/;
      return re.test(this.donationWidgetState.honoreeEmail);
    },
    validMoney(money) {
      // https://stackoverflow.com/questions/2227370/currency-validation
      var re = /^[1-9]\d*(((,\d{3}){1})?(\.\d{0,9})?)$/;
      return re.test(money);
    }
  },
}
</script>

<style scoped>
/* ─── Amount pills ────────────────────────────────────────────────
   Reactive selected state bound to Vuex amount, replacing the
   Tailwind peer-checked highlight so the $75 default doesn't
   flash gray during hydration. Typography: Barlow Condensed is an
   explicit exception to the v2 Red-Hat-only standardization — Rick
   wants the preset amounts in the condensed display face specifically
   because it reads as "pick an amount" more distinctively than Red
   Hat Display. Barlow Condensed is loaded on both v1 (legacy blade
   layouts) and v2 (app-campaign-v2.blade.php) for this reason. */
.amount-pill {
  display: block;
  width: 100%;
  padding: 16px 12px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  border: 1px solid #e5e7eb; /* gray-200 */
  border-radius: 6px;
  color: #000000;
  background: #ffffff;
  cursor: pointer;
  transition:
    background-color 240ms cubic-bezier(0.22, 1, 0.36, 1),
    color 240ms cubic-bezier(0.22, 1, 0.36, 1),
    border-color 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 240ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 240ms cubic-bezier(0.22, 1, 0.36, 1);
}
@media (min-width: 1024px) {
  .amount-pill { padding: 8px 24px; }
}
.amount-pill:hover {
  /* Per client direction: hover affordance switched from pink-300 to
     a darker variant of the active brand blue. The active pill is
     #009cde (from tw.css .switch-field input:checked + label); this
     hover sits one step darker at #0075a7 so the unselected pill,
     when hovered, previews the blue family without competing with
     the actually-selected pill's full saturation. */
  border-color: #0075a7;
  transform: translateY(-1px);
}
/* Active pill — at-rest blue matches the global .switch-field rule
   in resources/css/tw.css (#009cde). Co-located here so the scoped
   rule's higher specificity (Vue's [data-v-xxx] adds a class weight)
   doesn't accidentally flip the at-rest color back to pink if tw.css
   ever changes. */
.amount-pill--active {
  background: #009cde;
  color: #ffffff;
  border-color: #009cde;
  transform: none;
}
/* Per client direction: active pill on hover goes one shade darker
   blue rather than pink. #0075a7 is the established darker-blue
   token (already used in the campaign-create flow + trust-panel
   eyebrow) and sits ~25% darker than the at-rest #009cde, giving
   clear hover feedback without flickering to a different hue. */
.amount-pill--active:hover {
  background: #0075a7;
  color: #ffffff;
  border-color: #0075a7;
  transform: none;
}

/* Toggle-thumb shadow/pulse removed at client request — keep the thumb
   flat white against the pink track. Key-change still triggers Vue's
   transition via <transition :key="...">, just without a halo. */

/* ─── Recurring Monthly confirmation overlay ───────────────────────────
   Full-pane overlay INSIDE the form column of the donation widget.
   Forces the user to pick "Yes, continue monthly" or "No, one-time"
   before they can interact with the amount grid. Zero layout push —
   the form underneath stays exactly where it was. */
.monthly-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
}
.monthly-overlay__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.88);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
}
.monthly-overlay__panel {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 360px;
  background: white;
  border-radius: 1rem;
  padding: 1.75rem 1.5rem 1.5rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
  text-align: center;
}
.monthly-overlay__icon {
  width: 3.25rem;
  height: 3.25rem;
  margin: 0 auto 1.125rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  /* Per client direction: solid brand blue, no gradient. */
  background: #009cde;
  color: white;
  padding: 0.875rem;
  /* Heartbeat scale pulse only — no shadow bloom. Matches the widget's
     flat, hairline-bordered language while still signaling that this
     is the recurring/monthly indicator. */
  animation: monthly-overlay-pulse 2.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
@keyframes monthly-overlay-pulse {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.06); }
}
@media (prefers-reduced-motion: reduce) {
  .monthly-overlay__icon { animation: none; }
}
.monthly-overlay__icon svg { width: 100%; height: 100%; }
.monthly-overlay__eyebrow {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #009cde;
  margin: 0 0 0.5rem;
}
.monthly-overlay__title {
  font-size: 1.375rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.2;
  margin: 0 0 0.875rem;
}
.monthly-overlay__desc {
  font-size: 0.875rem;
  color: #4B5563;
  line-height: 1.5;
  margin: 0 0 1.5rem;
}
.monthly-overlay__desc strong {
  color: #009cde;
  font-weight: 700;
}
.monthly-overlay__actions {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.monthly-overlay__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6875rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s;
  border: 1px solid transparent;
}
/* Per client direction: solid brand blue (no gradient, no pink).
   Same color family as the icon above + the amount-pill active state
   on Step 1, keeping Step 1 entirely in the blue palette. Hover steps
   one shade darker to match the amount-pill hover pattern. */
.monthly-overlay__btn--primary {
  background: #009cde;
  color: white;
}
.monthly-overlay__btn--primary:hover {
  background: #0075a7;
  transform: translateY(-1px);
}
.monthly-overlay__btn--primary:active {
  background: #0075a7;
  transform: translateY(0) scale(0.98);
}
.monthly-overlay__btn--primary:focus-visible {
  outline: 2px solid #0075a7;
  outline-offset: 2px;
}
.monthly-overlay__btn--ghost {
  background: transparent;
  color: #4B5563;
  border-color: #E5E7EB;
}
.monthly-overlay__btn--ghost:hover {
  color: #009cde;
  border-color: #009cde;
  background: #f2fafd;
}
.monthly-overlay__btn--ghost:active {
  transform: scale(0.98);
}

/* Overlay enter/leave */
.monthly-overlay-enter-active { transition: opacity 220ms cubic-bezier(0.4, 0, 0.2, 1); }
.monthly-overlay-enter-active .monthly-overlay__panel {
  animation: monthly-overlay-panel-in 340ms cubic-bezier(0.16, 1, 0.3, 1) both;
}
.monthly-overlay-leave-active { transition: opacity 160ms ease; }
.monthly-overlay-leave-active .monthly-overlay__panel {
  animation: monthly-overlay-panel-out 160ms ease both;
}
.monthly-overlay-enter-from, .monthly-overlay-leave-to { opacity: 0; }

@keyframes monthly-overlay-panel-in {
  from { opacity: 0; transform: translateY(14px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0)    scale(1); }
}
@keyframes monthly-overlay-panel-out {
  from { opacity: 1; transform: translateY(0)  scale(1); }
  to   { opacity: 0; transform: translateY(6px) scale(0.98); }
}
@keyframes monthly-overlay-spin {
  from { transform: rotate(0); }
  to   { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .monthly-overlay__icon { animation: none !important; }
  .monthly-overlay-enter-active,
  .monthly-overlay-leave-active,
  .monthly-overlay-enter-active .monthly-overlay__panel,
  .monthly-overlay-leave-active .monthly-overlay__panel {
    animation-duration: 80ms !important;
    transition-duration: 80ms !important;
  }
}

/* Small "Edit" link that appears next to the main-form checkbox
   when a tribute has been saved. One-click reopen, keeps the
   checkbox visually checked (signifying tribute saved). */
.tribute-edit-link {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #DA1884;
  background: transparent;
  border: none;
  padding: 2px 8px;
  border-radius: 9999px;
  cursor: pointer;
  transition: background-color 160ms ease, color 160ms ease;
}
.tribute-edit-link:hover {
  background: rgba(218, 24, 132, 0.08);
}
.tribute-edit-link:focus-visible {
  outline: 2px solid #DA1884;
  outline-offset: 2px;
}

/* ─── Tribute swing ──────────────────────────────────────────────
   In-honor / in-memory dedication panel. Slides up from the bottom
   of the form column with a backdrop dim behind it. Mount-driven
   animation via .is-open / .is-closing class modifiers (vs. Vue
   <Transition>) so the backdrop + panel choreograph independently. */
/* Outer container spans the full form column. Always in the DOM so
   CSS transitions fire on is-open toggle. pointer-events:none by
   default keeps the form underneath interactive while the tribute
   is closed (otherwise the invisible backdrop would swallow clicks
   on the main-form checkbox, which was the re-open bug). */
.tribute-swing {
  position: absolute;
  inset: 0;
  z-index: 30;
  pointer-events: none;
}

.tribute-swing__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(17, 24, 39, 0.35);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  opacity: 0;
  pointer-events: none;
  transition: opacity 420ms cubic-bezier(0.4, 0, 0.2, 1);
}
.tribute-swing.is-open .tribute-swing__backdrop {
  opacity: 1;
  pointer-events: auto;
}

/* Panel fills the entire form column (inset:0 — top to bottom,
   edge to edge). Slides in from translateY(100%); same curve both
   directions, so close mirrors open. Flex column so the actions
   footer can pin to the bottom outside the scroll region. */
.tribute-swing__panel {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;                /* scroll happens inside __scroll */
  background: linear-gradient(180deg, #ffffff 0%, #FFF9FC 100%);
  box-shadow:
    0 -20px 40px -12px rgba(218, 24, 132, 0.14),
    0 -12px 24px -12px rgba(10, 13, 28, 0.18);
  font-family: 'Red Hat Display', system-ui, sans-serif;
  transform: translateY(100%);
  opacity: 0;
  pointer-events: none;
  transition:
    transform 640ms cubic-bezier(0.4, 0, 0.2, 1),
    opacity   520ms cubic-bezier(0.4, 0, 0.2, 1);
}
.tribute-swing.is-open .tribute-swing__panel {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
}

/* ─── Stage layout ─────────────────────────────────────────────
   Each stage is its own flex column owning the full panel. The
   <transition> swaps stages with a soft cross-fade + lift so the
   step change reads as one continuous gesture. */
.tribute-swing__stage {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 26px 26px 22px;
}
.tribute-swing__stage--choice {
  /* Step 1: hero choice. Centered, generous breathing room around
     the heart icon and toggle so the decision feels weighty. */
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 22px;
}
.tribute-swing__stage--form {
  /* Step 2: form-fill. Generous top padding per client mockup
     (image10) so the IN MEMORY OF chip sits below the panel's
     top edge instead of hugging it — gives the form a deliberate
     "header zone" feel. */
  padding-top: 36px;
  gap: 14px;
}

/* ─── Step 1 — Hero header ─────────────────────────────────────
   Flat heart icon over eyebrow/title stack. Halo + spinning dashed
   ring were dropped at client request — the rest of the modal is
   flat (no gradients, no decorative rings), so the tribute hero
   matches that pattern instead of standing apart. */
.tribute-swing__hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  padding: 4px 8px 0;
}
.tribute-swing__halo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
}
/* Halo ring + spinning border retired — keep the selector inert
   so any lingering markup degrades cleanly without re-introducing
   the gradient/animation. */
.tribute-swing__halo-ring { display: none; }
.tribute-swing__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  color: #ffffff;
}
.tribute-swing__icon--hero {
  width: 64px;
  height: 64px;
  background: #642F6C;
}
.tribute-swing__icon--inline {
  /* Used in Step 2 form-header next to "Tell us about them". */
  width: 28px;
  height: 28px;
  background: rgba(100, 47, 108, 0.1);
  color: #642F6C;
}
.tribute-swing__eyebrow {
  display: block;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 13px; font-weight: 700;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #642F6C;
  line-height: 1;
}
.tribute-swing__title {
  margin: 6px 0 6px;
  font-size: 26px; font-weight: 800;
  color: #1d2549;
  letter-spacing: -0.015em;
  line-height: 1.1;
}
.tribute-swing__desc {
  margin: 0 auto;
  max-width: 32ch;
  font-size: 14px; color: #6b7280;
  line-height: 1.5;
}

/* ─── Segmented toggle ─────────────────────────────────────────
   Two sizes: default (legacy) and --lg used on Step 1 where the
   toggle is the centerpiece. */
.tribute-swing__segmented {
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: #F3F4F6;
  border-radius: 9999px;
  padding: 3px;
  overflow: hidden;
  width: 100%;
}
.tribute-swing__segmented--lg {
  max-width: 320px;
  margin: 4px auto 0;
  padding: 4px;
  background: #F3F4F6;
}
.tribute-swing__seg-btn {
  position: relative;
  z-index: 1;
  padding: 6px 10px;
  background: transparent;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  font-family: 'Red Hat Display', system-ui, sans-serif;
  font-size: 13px; font-weight: 600;
  color: #6b7280;
  transition: color 220ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tribute-swing__segmented--lg .tribute-swing__seg-btn {
  padding: 13px 14px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.tribute-swing__seg-btn.is-active { color: #ffffff; }
.tribute-swing__seg-indicator {
  position: absolute;
  top: 3px; bottom: 3px; left: 3px;
  width: calc(50% - 3px);
  background: #642F6C;
  border-radius: 9999px;
  transition: transform 380ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tribute-swing__segmented--lg .tribute-swing__seg-indicator {
  top: 4px; bottom: 4px; left: 4px;
  width: calc(50% - 4px);
}

/* Tribute Step 2 back affordance uses the global .dw-back-pill
   defined in MakeDonationWidget.vue (single-source styling for
   every back button across the widget). Only the alignment is
   tribute-specific — anchor it to the left edge of the form
   stage so it reads as a "you came from" header chip. */
.tribute-swing__back-anchor { align-self: flex-start; }

.tribute-swing__form-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0 2px;
}
.tribute-swing__form-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #1d2549;
  letter-spacing: -0.01em;
  line-height: 1.15;
}

.tribute-swing__fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-right: 2px;
}
.tribute-swing__fields::-webkit-scrollbar { display: none; }
.tribute-swing__field { margin: 0; padding: 0; border: 0; }
.tribute-swing__label {
  display: block;
  font-size: 13px; font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
  line-height: 1.2;
  letter-spacing: 0.01em;
}
.tribute-swing__input {
  width: 100%;
  padding: 11px 14px;
  font-size: 15px; color: #111827;
  background: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  outline: none;
  transition:
    border-color 200ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow   200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tribute-swing__input:focus {
  /* Purple focus ring — inputs only render inside Step 2, where
     the panel is reskinned purple per client feedback. */
  border-color: #B197BD;
  box-shadow: 0 0 0 3px rgba(100, 47, 108, 0.14);
}
.tribute-swing__input.has-error {
  border-color: #EF4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.12);
}
.tribute-swing__textarea { resize: vertical; min-height: 84px; line-height: 1.45; }

/* Mobile: bump tribute inputs to 16px so iOS Safari doesn't zoom-on-focus
   when the field gains focus (the <16px trigger), plus a small legibility
   win. Client feedback 2026-05-29. Desktop keeps the 15px widget standard. */
@media (max-width: 640px) {
  .tribute-swing__input { font-size: 16px; } /* 15 → 16 (iOS no-zoom) */
}

/* ─── Per-stage actions ────────────────────────────────────────
   Each stage owns its own action row at the bottom. Step 1 has
   Continue + Cancel; Step 2 has Save tribute + Cancel. The old
   pinned global footer is gone — every screen has exactly one
   primary CTA so there's nothing redundant for the user to scan. */
.tribute-swing__step-actions {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  margin-top: auto;
  padding-top: 12px;
}
.tribute-swing__stage--choice .tribute-swing__step-actions {
  width: 100%;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
}
.tribute-swing__save {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 28px;
  background: #642F6C;
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  font-family: 'Red Hat Display', sans-serif;
  font-size: 16px; font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background-color 200ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tribute-swing__save--block { width: 100%; }
.tribute-swing__save:hover {
  background: #4A2151;
  transform: translateY(-1px);
}
.tribute-swing__save:active { transform: translateY(0) scale(0.98); }
.tribute-swing__save:focus-visible { outline: 2px solid #4A2151; outline-offset: 2px; }

.tribute-swing__cancel {
  padding: 10px 16px;
  background: transparent;
  border: none;
  font-size: 14px; font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: color 180ms;
  align-self: center;
}
.tribute-swing__cancel:hover { color: #642F6C; }
.tribute-swing__cancel:focus-visible {
  outline: 2px solid #642F6C; outline-offset: 2px; border-radius: 6px;
}

/* ─── Step transition ──────────────────────────────────────────
   Soft cross-fade + 6px lift between Step 1 and Step 2. Mode is
   out-in (template) so the leaving stage finishes before the new
   one mounts — no double-stack flash. */
.tribute-step-enter-active,
.tribute-step-leave-active {
  transition:
    opacity 280ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1);
}
.tribute-step-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.tribute-step-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

@media (prefers-reduced-motion: reduce) {
  .tribute-swing__panel,
  .tribute-swing.is-closing .tribute-swing__panel { transition-duration: 120ms !important; }
  .tribute-swing__backdrop { transition-duration: 100ms !important; }
  .tribute-swing__seg-indicator { transition-duration: 100ms; }
  .tribute-step-enter-active,
  .tribute-step-leave-active { transition-duration: 100ms; }
}
</style>
